import React, { useState } from 'react';
import { MapPin, Check, X, Loader2, Truck, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PincodeChecker({ onAvailable, onUnavailable, className = '' }) {
  const [pincode, setPincode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const checkPincode = async () => {
    if (pincode.length !== 6) {
      setResult({
        available: false,
        message: 'Please enter a valid 6-digit pincode'
      });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/check-pincode/${pincode}`);
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        
        if (data.available) {
          onAvailable?.(data);
        } else {
          onUnavailable?.(data);
        }
      }
    } catch (error) {
      setResult({
        available: false,
        message: 'Unable to check pincode. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      checkPincode();
    }
  };

  return (
    <div className={`bg-gray-50 rounded-xl p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="w-5 h-5 text-blue-600" />
        <span className="font-medium text-gray-700">Check Delivery Availability</span>
      </div>
      
      <div className="flex gap-2">
        <input
          type="text"
          value={pincode}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
            setPincode(value);
            if (value.length < 6) setResult(null);
          }}
          onKeyPress={handleKeyPress}
          placeholder="Enter 6-digit pincode"
          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center font-mono tracking-wider"
          maxLength={6}
        />
        <button
          onClick={checkPincode}
          disabled={loading || pincode.length !== 6}
          className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            'Check'
          )}
        </button>
      </div>

      {/* Result */}
      {result && (
        <div className={`mt-4 p-4 rounded-lg ${
          result.available 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-start gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              result.available ? 'bg-green-100' : 'bg-red-100'
            }`}>
              {result.available ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <X className="w-5 h-5 text-red-600" />
              )}
            </div>
            <div className="flex-1">
              <p className={`font-medium ${result.available ? 'text-green-800' : 'text-red-800'}`}>
                {result.message}
              </p>
              
              {result.available && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-green-700">
                    <Clock className="w-4 h-4" />
                    <span>Delivery in {result.estimated_days} day(s)</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-green-700">
                    <Truck className="w-4 h-4" />
                    <span>
                      {result.delivery_charge === 0 
                        ? 'Free Delivery' 
                        : `Delivery charge: ₹${result.delivery_charge}`
                      }
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-2">
                    {result.cod_available && (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                        ✓ COD Available
                      </span>
                    )}
                    {result.express_delivery && (
                      <span className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-full">
                        ⚡ Same Day Delivery
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PincodeChecker;
